/*
 * Allowlist HTML sanitiser for article bodies.
 *
 * Article content is rendered with dangerouslySetInnerHTML, so everything that
 * reaches it must be filtered. This runs at three points:
 *   1. on paste into the editor  (strips Word/Docs/webpage cruft and scripts)
 *   2. on save                   (the stored value is already clean)
 *   3. before preview            (what you see is what will be published)
 *
 * It is an ALLOWLIST: anything not named below is dropped. Unknown *formatting*
 * tags are unwrapped (their text survives); unknown *active* tags are removed
 * whole, children and all.
 *
 * It also normalises toward the semantic tags the public article stylesheet is
 * built for — <b> becomes <strong>, <div> becomes <p>, <font color> becomes a
 * styled <span>, and a bare <pre> gets its <code> wrapper — so content typed in
 * the editor renders identically to hand-authored HTML.
 *
 * This is defence in depth, not a substitute for sanitising on write in the
 * backend. See README → "Content sanitisation".
 */

import { ALLOWED_CLASSES } from '../constants/editorStyles';

// tag → attributes allowed on it (beyond the global ones below).
const ALLOWED_TAGS = {
  p: [],
  br: [],
  hr: [],
  h2: [],
  h3: [],
  h4: [],
  strong: [],
  em: [],
  u: [],
  s: [],
  mark: [],
  span: [],
  sub: [],
  sup: [],
  ul: [],
  ol: [],
  li: [],
  blockquote: [],
  figure: [],
  figcaption: [],
  pre: [],
  code: [],
  a: ['href', 'title', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height', 'loading', 'decoding'],
  table: [],
  thead: [],
  tbody: [],
  tr: [],
  th: ['colspan', 'rowspan'],
  td: ['colspan', 'rowspan'],
};

// Allowed on any permitted tag. `class` values are additionally filtered against
// ALLOWED_CLASSES, so an arbitrary class name cannot be used to target the site's
// own CSS.
const GLOBAL_ATTRS = ['style', 'class'];

// Dropped entirely, including their contents. Unwrapping these would leak
// script source or stylesheet text into the article as visible text.
const STRIP_WHOLE = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'applet',
  'noscript',
  'template',
  'link',
  'meta',
  'title',
  'base',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'svg',
  'math',
]);

// Non-semantic or legacy tags mapped to the equivalent we do allow.
const RENAME = {
  b: 'strong',
  i: 'em',
  strike: 's',
  del: 's',
  ins: 'u',
  h1: 'h2', // the page's <h1> is the article title; a body h1 would duplicate it
  h5: 'h4',
  h6: 'h4',
};

// Generic containers. They become <p> when they hold only inline content, and
// are unwrapped when they hold blocks — renaming a div full of paragraphs to <p>
// would nest <p> inside <p>, which browsers silently split apart on render.
const GENERIC_BLOCKS = new Set(['div', 'section', 'article', 'main', 'header', 'footer', 'aside']);

const BLOCK_CHILD = 'p,h1,h2,h3,h4,h5,h6,ul,ol,li,blockquote,pre,figure,table,hr,div,section,article';

// CSS properties worth keeping from a rich-text editor. Everything else
// (position, display, url(), behaviour, …) is dropped.
const ALLOWED_STYLES = new Set([
  'color',
  'background-color',
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
]);

/**
 * Reject any CSS value that could execute or fetch something.
 * `url(...)`, `expression(...)`, escapes and comments are all out; rgb()/rgba()
 * and hsl()/hsla() are the only function calls allowed through.
 */
function safeCssValue(value) {
  const v = String(value).trim();
  if (!v || v.length > 120) return null;
  if (/url\s*\(|expression\s*\(|javascript:|@import|\\|\/\*|<|>/i.test(v)) return null;
  const fnMatches = v.match(/[a-z-]+\s*\(/gi) || [];
  if (fnMatches.some((fn) => !/^(rgb|rgba|hsl|hsla)\s*\($/i.test(fn))) return null;
  return v;
}

function sanitizeStyle(styleValue) {
  const kept = [];
  for (const decl of String(styleValue).split(';')) {
    const idx = decl.indexOf(':');
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    if (!ALLOWED_STYLES.has(prop)) continue;
    const val = safeCssValue(decl.slice(idx + 1));
    if (val) kept.push(`${prop}: ${val}`);
  }
  return kept.join('; ');
}

/*
 * Whitespace and control characters, stripped before a URL scheme is checked.
 * Browsers ignore them when resolving a URL, so without this `java\nscript:`
 * and `  javascript:` both slip past a naive prefix test.
 */
// eslint-disable-next-line no-control-regex
const CTRL_AND_SPACE = /[\s\u0000-\u001f\u007f]/g;

/**
 * Only http(s), mailto, tel and same-document anchors for links.
 */
function safeHref(value) {
  const v = String(value).replace(CTRL_AND_SPACE, '').toLowerCase();
  if (v.startsWith('#') || v.startsWith('/')) return String(value).trim();
  if (/^(https?:|mailto:|tel:)/.test(v)) return String(value).trim();
  return null;
}

/** Images may be remote (http/https) or an inline data: image (draft mode). */
function safeSrc(value) {
  const v = String(value).replace(CTRL_AND_SPACE, '').toLowerCase();
  if (v.startsWith('/')) return String(value).trim();
  if (/^https?:/.test(v)) return String(value).trim();
  if (/^data:image\/(png|jpe?g|gif|webp|avif);base64,/.test(v)) return String(value).trim();
  return null;
}

/** Replace an element with its children, preserving the text. */
function unwrap(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function renameElement(el, newTag) {
  const replacement = el.ownerDocument.createElement(newTag);
  for (const attr of Array.from(el.attributes)) {
    replacement.setAttribute(attr.name, attr.value);
  }
  while (el.firstChild) replacement.appendChild(el.firstChild);
  el.parentNode.replaceChild(replacement, el);
  return replacement;
}

function cleanElement(el) {
  let tag = el.tagName.toLowerCase();

  if (STRIP_WHOLE.has(tag)) {
    el.remove();
    return;
  }

  // <font color="red"> → <span style="color: red">. Legacy, but it is still
  // what some editors and pasted email HTML produce.
  if (tag === 'font') {
    const color = el.getAttribute('color');
    el = renameElement(el, 'span');
    tag = 'span';
    if (color) el.setAttribute('style', `color: ${color}`);
  } else if (GENERIC_BLOCKS.has(tag)) {
    if (el.querySelector(BLOCK_CHILD)) {
      unwrap(el);
      return;
    }
    el = renameElement(el, 'p');
    tag = 'p';
  } else if (RENAME[tag]) {
    el = renameElement(el, RENAME[tag]);
    tag = RENAME[tag];
  }

  if (!ALLOWED_TAGS[tag]) {
    unwrap(el);
    return;
  }

  const allowed = new Set([...ALLOWED_TAGS[tag], ...GLOBAL_ATTRS]);

  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();

    // Drops every on* handler and anything else not explicitly permitted.
    if (!allowed.has(name)) {
      el.removeAttribute(attr.name);
      continue;
    }

    if (name === 'style') {
      const style = sanitizeStyle(attr.value);
      style ? el.setAttribute('style', style) : el.removeAttribute('style');
    } else if (name === 'class') {
      const kept = attr.value.split(/\s+/).filter((c) => ALLOWED_CLASSES.has(c));
      kept.length ? el.setAttribute('class', kept.join(' ')) : el.removeAttribute('class');
    } else if (name === 'href') {
      const href = safeHref(attr.value);
      href ? el.setAttribute('href', href) : el.removeAttribute('href');
    } else if (name === 'src') {
      const src = safeSrc(attr.value);
      /*
       * An image with an unusable src is dropped whole rather than left as a
       * broken-image icon in the article. Returning here is correct only in that
       * branch — the element no longer exists, so there is nothing to normalise.
       *
       * Returning on the SUCCESS path (as this did) skipped the `tag === 'img'`
       * normalisation below for every image that actually had a src, i.e. every
       * real image, so loading="lazy"/decoding="async" were never applied. That
       * went unnoticed because the editor writes loading="lazy" itself — but the
       * backend's allowlist does not carry `loading`, so it was stripped on save
       * and never restored on read.
       */
      if (!src) {
        el.remove();
        return;
      }
      el.setAttribute('src', src);
    }
  }

  // External links open in a new tab; rel prevents the opened page from
  // reaching back into window.opener.
  if (tag === 'a') {
    const href = el.getAttribute('href') || '';
    if (/^https?:/i.test(href)) {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    } else {
      el.removeAttribute('target');
      el.removeAttribute('rel');
    }
    // A link with no destination is just text.
    if (!href) unwrap(el);
  }

  // Lazy-load body images; the stylesheet already reserves their box.
  if (tag === 'img') {
    el.setAttribute('loading', 'lazy');
    el.setAttribute('decoding', 'async');
    if (!el.hasAttribute('alt')) el.setAttribute('alt', '');
  }

  // The article stylesheet targets `pre code`, so a bare <pre> would render
  // without monospace styling.
  if (tag === 'pre' && !el.querySelector('code')) {
    const code = el.ownerDocument.createElement('code');
    while (el.firstChild) code.appendChild(el.firstChild);
    el.appendChild(code);
  }
}

/**
 * @param {string} html untrusted HTML
 * @returns {string} HTML containing only allowlisted tags, attributes and styles
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // Node (e.g. a build script). Better to store nothing than to store
    // unfiltered HTML and assume someone downstream will catch it.
    return '';
  }

  const doc = new DOMParser().parseFromString(String(html), 'text/html');

  // Snapshot the list first: cleanElement unwraps and removes nodes, and a live
  // NodeList would shift underneath the loop.
  const elements = Array.from(doc.body.querySelectorAll('*'));
  for (const el of elements) {
    // Skip nodes already detached by an ancestor being removed.
    if (el.isConnected) cleanElement(el);
  }

  return doc.body.innerHTML.trim();
}

/** Plain text from HTML — used for word counts and excerpt suggestions. */
export function htmlToText(html) {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return String(html).replace(/<[^>]*>/g, ' ');
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}
